<template>
  <v-card class="mb-6">
    <v-row dense>
      <v-col cols="4">
        <v-card-title>
          部位
        </v-card-title>
        <v-card-subtitle
          class="black--text pt-3"
          v-bind="diffAttributes(previousObject.name, item.name)">
          <edit-dialog
            v-model="item.name"
            :error-messages="error[`rooms.${roomIndex}.parts.${index}.name`]">
            <template #text>
              <span style="font-size: 1.5em">
                {{ item.name }}
              </span>
            </template>
          </edit-dialog>
          <div class="previous-value">
            {{ previousValue(previousObject.name, item.name) }}
          </div>
        </v-card-subtitle>
      </v-col>
      <v-col cols="4">
        <v-card-title>
          改修年
        </v-card-title>
        <v-card-subtitle
          class="black--text"
          v-bind="diffAttributes(previousObject.renovation_year, item.renovation_year)">
          <select-year
            v-model="item.renovation_year"
            :error-messages="error[`rooms.${roomIndex}.parts.${index}.renovation_year`]" />
          <div class="previous-value">
            {{ previousValue(previousObject.renovation_year, item.renovation_year) }}
          </div>
        </v-card-subtitle>
      </v-col>
    </v-row>
    <v-row dense>
      <v-col cols="12">
        <v-card :elevation="0">
          <v-row v-if="item.surveys.length > 0">
            <v-col>
              <v-btn
                dense
                small
                text
                @click.prevent="$emit('cycle')">
                <v-icon small>mdi-reload</v-icon>
                書面調査情報切替
              </v-btn>
            </v-col>
          </v-row>
          <v-data-table
            hide-default-footer
            :headers="headers"
            :items="item.surveys"
            disable-sort
            :hide-default-header="item.surveys.length === 0"
            no-data-text="現地調査情報が入力されていません +ボタンで追加してください。"
            :item-class="itemRowClass"
            dense>
            <template #item.written_info="props">
              <span v-if="props.item.id">
                {{ getWrittenInfo(props.item) }}
              </span>
              <span
                v-else
                class="subtitle-2">
                書類調査なし
              </span>
            </template>
            <template #item.is_consistent="props">
              <div
                v-bind="diffAttributes(
                  getPreviousSurvey(props.item, 'is_consistent'), props.item.is_consistent)">
                <select-dialog
                  v-model="props.item.is_consistent"
                  :select-items="consistents"
                  item-text="value"
                  item-value="id"
                  @change="onConsistentChanged(props.item)" />
                <div class="previous-value">
                  {{ previousValue(
                    getPreviousSurvey(props.item, 'is_consistent'),
                    props.item.is_consistent,
                    consistents,
                  ) }}
                </div>
              </div>
            </template>
            <template #item.field_material="props">
              <div v-if="fieldDisabled(props.item)">
                現地調査時この部位はなしと確認。
              </div>
              <div
                v-else
                v-bind="diffAttributes(
                  getPreviousSurvey(props.item, 'field_material'), props.item.field_material)">
                <edit-dialog
                  v-model="props.item.field_material">
                  <v-text-field
                    v-model="props.item.field_material"
                    class="mt-5"
                    width="150px"
                    label="現地確認建材名・商品名"
                    counter
                    autofocus
                    outlined
                    dense />
                  <v-checkbox
                    label="無し"
                    @change="(val) => {
                      onDisableFieldClicked(val, props.item)
                    }" />
                </edit-dialog>
                <div class="previous-value">
                  {{ previousValue(getPreviousSurvey(
                    props.item, 'field_material'), props.item.field_material) }}
                </div>
              </div>
            </template>
            <template #item.field_remarks="props">
              <div
                v-bind="diffAttributes(
                  getPreviousSurvey(props.item, 'field_remarks'),
                  props.item.field_remarks
                )">
                <edit-dialog
                  v-if="!fieldDisabled(props.item)"
                  v-model="props.item.field_remarks">
                  <v-combobox
                    v-model="props.item.field_remarks"
                    :items="remarksOptions"
                    class="mt-5"
                    width="150px"
                    label="裏面確認情報・その他"
                    counter
                    autofocus
                    outlined
                    dense />
                </edit-dialog>
                <div class="previous-value">
                  {{ previousValue(getPreviousSurvey(
                    props.item, 'field_remarks'), props.item.field_remarks) }}
                </div>
              </div>
            </template>
            <template #item.field_vs_no="props">
              <div
                v-bind="diffAttributes(
                  getPreviousSurvey(props.item, 'field_vs_no'), props.item.field_vs_no)">
                <select-dialog
                  v-if="!fieldDisabled(props.item)"
                  v-model="props.item.field_vs_no"
                  :select-items="fieldVsItems"
                  item-text="text"
                  item-value="value"
                  :select-width="100" />
                <div class="previous-value">
                  {{ previousValue(
                    getPreviousSurvey(props.item, 'field_vs_no'),
                    props.item.field_vs_no,
                    fieldVsItems,
                  ) }}
                </div>
              </div>
            </template>
            <template #item.field_sampling_id="props">
              <div
                v-bind="diffAttributes(
                  getPreviousSurvey(props.item, 'field_sampling_id'),
                  props.item.field_sampling_id)">
                <select-dialog
                  v-if="!fieldDisabled(props.item)"
                  v-model="props.item.field_sampling_id"
                  :select-items="samplings"
                  item-text="value"
                  item-value="id"
                  :select-width="100" />
                <div class="previous-value">
                  {{ previousValue(
                    getPreviousSurvey(props.item, 'field_sampling_id'),
                    props.item.field_sampling_id,
                    samplings,
                  ) }}
                </div>
              </div>
            </template>
            <template #item.field_sampling="props">
              <div
                v-bind="diffAttributes(
                  getPreviousSurvey(props.item, 'field_sampling'), props.item.field_sampling)">
                <edit-dialog
                  v-if="!fieldDisabled(props.item)"
                  v-model="props.item.field_sampling" />
                <div class="previous-value">
                  {{ previousValue(getPreviousSurvey(
                    props.item, 'field_sampling'), props.item.field_sampling) }}
                </div>
              </div>
            </template>
            <template #item.sampling_result="props">
              <div
                v-bind="diffAttributes(
                  getPreviousSurvey(props.item, 'sampling_result'), props.item.sampling_result)">
                <select-dialog
                  v-if="!fieldDisabled(props.item)"
                  v-model="props.item.sampling_result"
                  :select-items="samplingResults"
                  item-text="value"
                  item-value="id"
                  :select-width="100" />
                <div class="previous-value">
                  {{ previousValue(
                    getPreviousSurvey(props.item, 'sampling_result'),
                    props.item.sampling_result,
                    samplingResults,
                  ) }}
                </div>
              </div>
            </template>
            <template #item.field_status_id="props">
              <div
                v-bind="diffAttributes(
                  getPreviousSurvey(props.item, 'field_status_id'), props.item.field_status_id)">
                <select-dialog
                  v-if="!fieldDisabled(props.item)"
                  v-model="props.item.field_status_id"
                  :select-items="results"
                  item-text="value"
                  item-value="id"
                  :select-width="100" />
                <div class="previous-value">
                  {{ previousValue(
                    getPreviousSurvey(props.item, 'field_status_id'),
                    props.item.field_status_id,
                    results,
                  ) }}
                </div>
              </div>
            </template>
            <template
              v-if="!readonly"
              #item.delete="props">
              <v-btn
                class="delete-part"
                fab
                dark
                small
                color="red"
                @click="onDeletionButtonClicked(props.item)">
                <v-icon dark>mdi-minus</v-icon>
              </v-btn>
            </template>
            <template
              v-if="!readonly"
              #body.append>
              <tr>
                <td
                  colspan="10"
                  class="text-center">
                  <v-btn
                    class="add-part"
                    fab
                    dark
                    small
                    color="primary"
                    @click="onAddRowButtonClicked">
                    <v-icon dark>mdi-plus</v-icon>
                  </v-btn>
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>
    <confirm-dialog
      v-if="deletionConfirmDialog"
      v-model="deletionConfirmDialog"
      title="確認"
      @ok="onDeletionConfirmDialogOk"
      @cancel="deletionConfirmDialog=false">
      削除します。よろしいですか？
      <v-alert color="warning">
        この材料に対して書面調査の情報が記入済みの場合削除されます。
        この操作は元に戻せません。
      </v-alert>
    </confirm-dialog>
  </v-card>
</template>

<script>
import EditDialog from '../EditDialog'
import SelectDialog from '../SelectDialog'
import SelectYear from '../SelectYear'
import ConfirmDialog from '../ConfirmDialog'
import PreviousDiff from '../../mixins/PreviousDiff'

export default {
  name: 'FieldRoomRow',
  components: {
    SelectYear,
    EditDialog,
    SelectDialog,
    ConfirmDialog,
  },
  mixins: [PreviousDiff],
  props: {
    writtenFieldId: {
      type: Number,
      default: 0,
    },
    item: {
      type: Object,
      required: true,
    },
    index: {
      type: Number,
      required: true,
    },
    roomIndex: {
      type: Number,
      required: true,
    },
    readonly: {
      type: [Number, Boolean],
      default: false,
    },
    statuses: {
      type: Array,
      default: () => ([]),
    },
    reasons: {
      type: Array,
      default: () => ([]),
    },
    remarksOptions: {
      type: Array,
      default: () => ([]),
    },
    samplings: {
      type: Array,
      default: () => ([]),
    },
    results: {
      type: Array,
      default: () => ([]),
    },
    consistents: {
      type: Array,
      default: () => ([
        { id: 1, value: '○' },
        { id: 0, value: '×' },
      ]),
    },
    fieldVsItems: {
      type: Array,
      default: () => ([
        { text: '', value: null },
        { text: '有り', value: 'VS' },
        { text: '無し', value: 'NONE' },
      ]),
    },
    samplingResults: {
      type: Array,
      default: () => ([]),
    },
    error: {
      type: Object,
      default: () => ({}),
    },
    created: {
      type: Boolean,
      default: false,
    },
    applied: {
      type: Boolean,
      default: false,
    },
    diffDisabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      writtenFields: [
        { text: '書面調査[建材名・商品名]', value: 'written_material' },
        { text: '書面調査[備考]', value: 'written_remarks' },
        { text: '書面調査[図面の有無]', value: 'has_drawings' },
        { text: '書面調査[石綿含有の可能性]', value: 'written_status_id' },
        { text: '書面調査[判断根拠]', value: 'written_reason_id' },
        { text: '書面調査[分析予定]', value: 'analysis_scheduled' },
      ],
      headers: [
        { text: '書面調査[建材名・商品名]', value: 'written_info', width: '10%' },
        { text: '書面調査との整合性確認', value: 'is_consistent', width: '5%' },
        { text: '現地確認建材名・商品名', value: 'field_material', width: '20%' },
        { text: '裏面確認情報・その他', value: 'field_remarks', width: '20%' },
        { text: '実施VS', value: 'field_vs_no', width: '10%' },
        { text: '実施サンプリング', value: 'field_sampling_id', width: '10%' },
        { text: 'サンプリングNo', value: 'field_sampling', width: '10%' },
        { text: '代表サンプル・同等品', value: 'sampling_result', width: '5%' },
        { text: '現地調査時診断  結果', value: 'field_status_id', width: '5%' },
        { text: '', value: 'delete', width: '10%' },
      ],
      deletionConfirmDialog: false,
      deletionTargetItem: {},
    }
  },
  watch: {
    writtenFieldId() {
      this.headers[0].text = this.writtenFields[this.writtenFieldId].text
    },
    diffDisabled() {
      this.disableDiff = this.diffDisabled
    },
  },
  methods: {
    onAddRowButtonClicked() {
      this.item.surveys.push({
        is_consistent: 0,
        field_material: '',
        field_remarks: '',
        field_vs_no: '',
        field_sampling_id: '',
        field_sampling: '',
        sampling_result: '',
        field_status_id: '',
      })
    },
    onDeletionButtonClicked(item) {
      this.deletionTargetItem = item
      this.deletionConfirmDialog = true
    },
    onDeletionConfirmDialogOk() {
      const index = this.item.surveys.indexOf(this.deletionTargetItem)
      this.item.surveys.splice(index, 1)
      this.deletionConfirmDialog = false
    },
    getWrittenInfo(item) {
      const key = this.writtenFields[this.writtenFieldId].value
      if (key === 'written_status_id') {
        const [result] = this.statuses.filter(
          status => status.id === item.written_status_id,
        )

        return result ? result.value : ''
      }
      if (key === 'has_drawings') {
        const result = item.has_drawings ? '◯' : '×'

        return result
      }
      if (key === 'written_reason_id') {
        const [result] = this.reasons.filter(
          reason => reason.id === item.written_reason_id,
        )

        return result ? result.value : ''
      }

      return item[key] ?? ''
    },
    onConsistentChanged(survey) {
      const index = this.item.surveys.indexOf(survey)
      if (survey.is_consistent === 1) {
        // 整合性確認が○の場合は書面調査の建材名・商品名と備考がそのまま入る
        this.$set(this.item.surveys[index], 'field_material', survey.written_material)
        this.$set(this.item.surveys[index], 'field_remarks', survey.field_remarks)
      }
    },
    onDisableFieldClicked(disabled, item) {
      const surveyIndex = this.item.surveys.indexOf(item)
      const survey = this.item.surveys[surveyIndex]
      survey.field_material = disabled ? '無し' : ''
      if (disabled) {
        survey.field_information = ''
        survey.field_vs_no = ''
        survey.field_sampling = ''
        survey.sampling_result = ''
        survey.field_reason_id = ''
        survey.documents = ''
        survey.has_asbestos = ''
        survey.asbestos_type = ''
        survey.asbestos_level = ''
        this.$set(this.item.surveys, surveyIndex, survey)
      }
    },
    fieldDisabled(survey) {
      return survey.field_material === '無し'
    },
    itemRowClass(item) {
      return this.fieldDisabled(item) ? 'field-survey-table-disabled-row' : ''
    },
    getPreviousSurvey(object, columnName) {
      if (!this.previousObject || !this.previousObject.surveys) {
        return null
      }
      const target = this.previousObject.surveys.filter(item => item.id === object.id)[0] ?? null
      if (!target) {
        return ' '
      }
      const index = this.previousObject.surveys.indexOf(target)
      return this.previousObject && this.previousObject.surveys
        ? this.previousObject.surveys[index][columnName] ?? null
        : null
    },
    previousValue(previous, current, options = null) {
      const option = Array.isArray(options) ? options.filter(opt => opt.id === previous) : null
      return this.hasDiff(previous, current)
        ? `変更前: ${option && option[0] ? option[0].value : (previous ?? '')}`
        : ''
    },
    diffAttributes(previous, current, options = null) {
      return this.created && this.applied
        ? this.$super(PreviousDiff).diffAttributes(previous, current, options)
        : false
    },
    hasDiff(previous, current) {
      return this.created && this.applied
        ? this.$super(PreviousDiff).hasDiff(previous, current)
        : false
    },
  },
}
</script>

<style scoped>
@media screen and (max-width:1320px) {
  .v-btn {
    padding: 0px 4px !important;
  }
}
.add-part {
  width: 20px;
  height: 20px;
}
.delete-part {
  width: 20px;
  height: 20px;
}
.v-card__title {
  font-size: 1rem;
}
</style>

<style>
.col1 {
  background-color: #4D342E;
}
.field-survey-table-disabled-row TD {
  background-color: #d9d9d9;
}
.field-survey-table-disabled-row TD:first-of-type {
  background-color: white!important;
}

.field-survey-table-disabled-row TD:nth-of-type(2) {
  background-color: white!important;
}

</style>
